import { displayCommercePrice, formatCommerceMoney, parseCommercePrice } from './commerceCurrency';
import { parseTabItems, serializeTabItems, type TabItem } from './blockContentCodec';

export type ProductColorOption = { label: string; hex?: string; imageUrl?: string; price?: string };

/** Group of sizes that share one price (e.g. S,M,L → 20.95). */
export type SizePriceTier = { sizes: string[]; price: string };

/** Group of colors that share one price (e.g. Pink, Schwarz → 24.99). */
export type ColorPriceTier = { colors: string[]; price: string };

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const IMAGE_URL_RE = /^(\/api\/saas\/v1\/media\/|https?:\/\/127\.0\.0\.1)/i;
const PRICE_TOKEN_RE = /^€?\s*\d+([.,]\d{1,2})?\s*€?$/;

export function parseListField(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof raw !== 'string') return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Justbrand / Mega imports sometimes glue color rows with a single space after
 * `/file` instead of newline/comma:
 * `Hauptansicht|#64748b|/api/.../file Ansicht 2|#64748b|/api/.../file`
 * Without a split, the first image URL becomes `.../file Ansicht 2` and the PDP
 * gallery shows a broken selected slide.
 */
const SPACE_GLUED_COLOR_ROW_RE = /(?<=\/file)\s+(?=[^|\s][^|\n]*\|#)/i;

/**
 * Split color option rows. RUNEVER / multi-line imports use one color per line
 * (`Name|#hex|/media/...`); the product editor serializes with commas.
 * Prefer newline rows when present so `|`-delimited URLs are not smashed into one chip.
 * Also split space-glued `.../file Label|#hex` rows from older imports.
 */
export function parseColorOptionRows(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (/\r?\n/.test(trimmed)) {
    return trimmed.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }
  if (SPACE_GLUED_COLOR_ROW_RE.test(trimmed)) {
    const glued = trimmed.split(SPACE_GLUED_COLOR_ROW_RE).map((s) => s.trim()).filter(Boolean);
    if (glued.length > 1) return glued;
  }
  return parseListField(trimmed);
}

export function serializeSizes(sizes: string[]): string {
  return sizes.map((s) => s.trim()).filter(Boolean).join(', ');
}

/**
 * PDP gallery touch-swipe. Default **true** (matches SiteRenderer carousel UX).
 * Explicit false / "false" / 0 disables swipe (prev/next buttons still work).
 */
export function parseGallerySwipeable(value: unknown): boolean {
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return true;
}

/**
 * Product-level gallery ↔ options sync mode.
 * - `inherit` — use shop `commerce.galleryOptionsSync` (default false)
 * - `true` — gallery slide/swipe/autoplay updates selected color (and thus price tiers)
 * - `false` — gallery-only; color→image still works when buyer picks a color
 */
export type GalleryOptionsSyncMode = 'inherit' | boolean;

/**
 * Parse product data field. Unset / inherit → `'inherit'`.
 * Aliases: `syncGalleryWithVariants`, `gallery_options_sync`.
 */
export function parseGalleryOptionsSyncMode(value: unknown): GalleryOptionsSyncMode {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'on' || s === 'sync') return true;
    if (s === 'off' || s === 'gallery-only' || s === 'gallery_only') return false;
    if (s === 'inherit' || s === '') return 'inherit';
  }
  return 'inherit';
}

/** Shop default: false = gallery does not change color/size/price (safer cart UX). */
export function parseGalleryOptionsSyncShop(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

/** Resolve product override against shop default (default shop = false). */
export function resolveGalleryOptionsSync(
  productValue: unknown,
  shopDefault = false,
): boolean {
  const mode = parseGalleryOptionsSyncMode(productValue);
  if (mode === 'inherit') return Boolean(shopDefault);
  return mode;
}

function isLikelyImageUrl(value: string): boolean {
  const s = value.trim();
  if (!s || /\s/.test(s)) return false;
  if (IMAGE_URL_RE.test(s)) return true;
  return /\.(png|jpe?g|webp|gif|svg)(?:\?|#|$)/i.test(s);
}

/**
 * Take a clean image URL from a pipe token that may still carry glued label junk
 * (`/api/saas/v1/media/…/file Ansicht 2`).
 */
export function sanitizeProductImageUrl(value: string): string {
  const s = String(value || '').trim();
  if (!s) return '';
  const media = s.match(/^(\/api\/saas\/v1\/media\/[a-f0-9]+\/file)\b/i);
  if (media) return media[1];
  if (/\s/.test(s)) {
    const first = s.split(/\s+/)[0] || '';
    return isLikelyImageUrl(first) ? first : '';
  }
  return isLikelyImageUrl(s) ? s : '';
}

const COLOR_DRAFT_RE = /^__draft_(\d+)__$/i;

/**
 * Mega/import color rows often glue care copy onto the name, e.g.
 * `Schwarz Pflegehinweis: Maschinenwäsche 30°C` or `Camouflage Army Pflegehinweis: …`.
 * Split so chips show color only; care can go to Material & Pflege.
 */
const COLOR_CARE_SPLIT_RE = /\s+(pflegehinweis|pflegeleicht)\b/i;

export function splitProductColorLabel(raw: string): { label: string; care: string } {
  const s = String(raw || '').trim().replace(/\s+/g, ' ');
  if (!s) return { label: '', care: '' };
  const match = COLOR_CARE_SPLIT_RE.exec(s);
  if (!match || match.index === undefined || match.index <= 0) {
    return { label: s, care: '' };
  }
  const label = s.slice(0, match.index).trim();
  const care = s.slice(match.index).trim();
  if (!label) return { label: s, care: '' };
  return { label, care };
}

/** Care snippets embedded in dirty color labels (before parseColorOptions strips them). */
export function extractCareNotesFromColorField(raw: unknown): string[] {
  const notes: string[] = [];
  for (const entry of parseListField(raw)) {
    const labelPart = entry.split('|')[0] || '';
    const { care } = splitProductColorLabel(labelPart);
    if (care) notes.push(care);
  }
  return [...new Set(notes)];
}

/** Append unique care notes into Material & Pflege (or create that tab). */
export function enrichTabContentWithCareNotes(
  tabContent: string,
  careNotes: string[],
  careTabLabel = 'Material & Pflege',
): string {
  const unique = [...new Set(careNotes.map((n) => n.trim()).filter(Boolean))];
  if (!unique.length) return tabContent;
  const tabs = parseProductTabs(tabContent, '', 'Description');
  const careIdx = tabs.findIndex((t) => /pflege|material|care|washing/i.test(t.label));
  if (careIdx >= 0) {
    const existing = tabs[careIdx].content.trim();
    const additions = unique.filter((n) => !existing.includes(n));
    if (!additions.length) return tabContent;
    const next = [...tabs];
    next[careIdx] = {
      ...next[careIdx],
      content: existing ? `${existing}\n\n${additions.join('\n\n')}` : additions.join('\n\n'),
    };
    return serializeProductTabs(next);
  }
  return serializeProductTabs([
    ...tabs,
    { label: careTabLabel, content: unique.join('\n\n') },
  ]);
}

function looksLikePriceToken(value: string): boolean {
  const t = value.trim();
  if (!t || isLikelyImageUrl(t) || HEX_RE.test(t)) return false;
  if (PRICE_TOKEN_RE.test(t)) return true;
  return parseCommercePrice(t) > 0;
}

export function parseColorOptions(raw: unknown): ProductColorOption[] {
  const items = parseColorOptionRows(raw);
  return items.map((entry) => {
    const parts = entry.split('|').map((s) => s.trim());
    const rawLabel = parts[0] || '';
    const draftEmpty = COLOR_DRAFT_RE.test(rawLabel);
    const label = draftEmpty ? '' : splitProductColorLabel(rawLabel).label;
    let hex: string | undefined;
    let imageUrl: string | undefined;
    let price: string | undefined;
    for (let i = 1; i < parts.length; i += 1) {
      const part = parts[i];
      if (!part) continue;
      if (!hex && HEX_RE.test(part)) {
        hex = part;
        continue;
      }
      if (!imageUrl) {
        const cleaned = sanitizeProductImageUrl(part);
        if (cleaned) {
          imageUrl = cleaned;
          continue;
        }
      }
      if (!price && looksLikePriceToken(part)) {
        price = part;
      }
    }
    if (!hex && !imageUrl && !price && HEX_RE.test(entry.trim())) {
      return { label: entry.trim(), hex: entry.trim() };
    }
    return {
      label,
      ...(hex ? { hex } : {}),
      ...(imageUrl ? { imageUrl } : {}),
      ...(price ? { price } : {}),
    };
  });
}

export function serializeColorOptions(options: ProductColorOption[]): string {
  return options
    .map((opt, index) => {
      const label = (opt.label || '').trim();
      const hex = (opt.hex || '').trim();
      const imageUrl = (opt.imageUrl || '').trim();
      const price = (opt.price || '').trim();
      // Keep draft rows created by "Add color" before the user types a name.
      if (!label && !hex && !imageUrl && !price) return '';
      const name = label || `__draft_${index + 1}__`;
      if (hex && imageUrl && price) return `${name}|${hex}|${imageUrl}|${price}`;
      if (hex && imageUrl) return `${name}|${hex}|${imageUrl}`;
      if (hex && price) return `${name}|${hex}|${price}`;
      if (imageUrl && price) return `${name}|#00000000|${imageUrl}|${price}`;
      if (hex) return `${name}|${hex}`;
      if (imageUrl) return `${name}|#00000000|${imageUrl}`;
      if (price) return `${name}|${price}`;
      return name;
    })
    .filter(Boolean)
    .join(', ');
}

/** Parse size→price groups. Lines: `S, M, L | 20.95` (incomplete draft rows allowed). */
export function parseSizePriceTiers(raw: unknown): SizePriceTier[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const pipe = line.lastIndexOf('|');
      if (pipe < 0) return null;
      const sizesPart = line.slice(0, pipe).trim();
      const price = line.slice(pipe + 1).trim();
      const sizes = sizesPart.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
      // Bare `|` (or ` | `) is an editor draft row with no sizes/price yet.
      if (!sizes.length && !price && line.replace(/\s/g, '') !== '|') return null;
      return { sizes, price };
    })
    .filter((t): t is SizePriceTier => Boolean(t));
}

export function serializeSizePriceTiers(
  tiers: SizePriceTier[],
  opts?: { keepDrafts?: boolean },
): string {
  const keepDrafts = Boolean(opts?.keepDrafts);
  return tiers
    .map((tier) => {
      const sizes = tier.sizes.map((s) => s.trim()).filter(Boolean);
      const price = (tier.price || '').trim();
      if (!sizes.length && !price) return keepDrafts ? '|' : '';
      if (!sizes.length || !price) {
        return keepDrafts ? `${sizes.join(', ')} | ${price}` : '';
      }
      return `${sizes.join(', ')} | ${price}`;
    })
    .filter(Boolean)
    .join('\n');
}

/** Parse color→price groups. Lines: `Pink, Schwarz | 24.99` (incomplete draft rows allowed). */
export function parseColorPriceTiers(raw: unknown): ColorPriceTier[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const pipe = line.lastIndexOf('|');
      if (pipe < 0) return null;
      const colorsPart = line.slice(0, pipe).trim();
      const price = line.slice(pipe + 1).trim();
      const colors = colorsPart.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
      // Bare `|` (or ` | `) is an editor draft row with no colors/price yet.
      if (!colors.length && !price && line.replace(/\s/g, '') !== '|') return null;
      return { colors, price };
    })
    .filter((t): t is ColorPriceTier => Boolean(t));
}

export function serializeColorPriceTiers(
  tiers: ColorPriceTier[],
  opts?: { keepDrafts?: boolean },
): string {
  const keepDrafts = Boolean(opts?.keepDrafts);
  return tiers
    .map((tier) => {
      const colors = tier.colors.map((c) => c.trim()).filter(Boolean);
      const price = (tier.price || '').trim();
      if (!colors.length && !price) return keepDrafts ? '|' : '';
      if (!colors.length || !price) {
        return keepDrafts ? `${colors.join(', ')} | ${price}` : '';
      }
      return `${colors.join(', ')} | ${price}`;
    })
    .filter(Boolean)
    .join('\n');
}

/** Global / bulk fill target for variant pricing shortcuts. */
export type GlobalVariantPriceMode = 'base' | 'sizes' | 'colors' | 'all';

/** True when at least one size tier has both sizes and a positive price. */
export function hasFilledSizePriceTiers(tiers: SizePriceTier[]): boolean {
  return tiers.some(
    (t) => t.sizes.some((s) => s.trim()) && parseCommercePrice(t.price) > 0,
  );
}

/** True when at least one color tier has both labels and a positive price. */
export function hasFilledColorPriceTiers(tiers: ColorPriceTier[]): boolean {
  return tiers.some(
    (t) => t.colors.some((c) => c.trim()) && parseCommercePrice(t.price) > 0,
  );
}

/** One size price tier covering every listed size at the same price. */
export function buildGlobalSizePriceTiers(sizes: string[], price: string): SizePriceTier[] {
  const cleaned = sizes.map((s) => s.trim()).filter(Boolean);
  const p = (price || '').trim();
  if (!cleaned.length || !p) return [];
  return [{ sizes: cleaned, price: p }];
}

/** One color price tier covering every listed color at the same price. */
export function buildGlobalColorPriceTiers(colorLabels: string[], price: string): ColorPriceTier[] {
  const cleaned = colorLabels.map((c) => c.trim()).filter(Boolean);
  const p = (price || '').trim();
  if (!cleaned.length || !p) return [];
  return [{ colors: cleaned, price: p }];
}

/**
 * Build a form patch for the “Alle Varianten” global fill actions.
 * Modes write `price` and/or replace size/color tiers with a single all-inclusive tier
 * so `resolveVariantPrice` returns that amount for every size/color.
 */
export function applyGlobalVariantPricePatch(args: {
  price: string;
  sizes: string[];
  colorLabels: string[];
  mode: GlobalVariantPriceMode;
}): Partial<Pick<ProductFormState, 'price' | 'sizePriceTiers' | 'colorPriceTiers'>> {
  const price = (args.price || '').trim();
  const out: Partial<Pick<ProductFormState, 'price' | 'sizePriceTiers' | 'colorPriceTiers'>> = {};
  // Empty or non-positive → no-op (UI also blocks, but keep helpers safe for bulk callers).
  if (!price || parseCommercePrice(price) <= 0) return out;

  if (args.mode === 'base' || args.mode === 'all') {
    out.price = price;
  }
  if (args.mode === 'sizes' || args.mode === 'all') {
    const tiers = buildGlobalSizePriceTiers(args.sizes, price);
    // Never clear existing tiers when there are no sizes/colors to cover.
    if (tiers.length) {
      out.sizePriceTiers = serializeSizePriceTiers(tiers);
    }
  }
  if (args.mode === 'colors' || args.mode === 'all') {
    const tiers = buildGlobalColorPriceTiers(args.colorLabels, price);
    if (tiers.length) {
      out.colorPriceTiers = serializeColorPriceTiers(tiers);
    }
  }
  return out;
}

/** Apply global fill onto a product form using that product’s own sizes/colors. */
export function applyGlobalVariantPriceToForm(
  form: ProductFormState,
  price: string,
  mode: GlobalVariantPriceMode,
): ProductFormState {
  const sizes = parseListField(form.sizes);
  const colorLabels = parseColorOptions(form.colors)
    .map((c) => (c.label || '').trim())
    .filter(Boolean);
  const patch = applyGlobalVariantPricePatch({ price, sizes, colorLabels, mode });
  return { ...form, ...patch };
}

/** Copy base + size/color price tiers from one product form onto another. */
export function copyVariantPricesFromForm(
  target: ProductFormState,
  source: ProductFormState,
): ProductFormState {
  return {
    ...target,
    price: source.price,
    sizePriceTiers: source.sizePriceTiers,
    colorPriceTiers: source.colorPriceTiers,
  };
}

/**
 * Effective shop price for the selected size/color.
 * Priority: size tier → color tier → individual color.price → base.
 */
export function resolveVariantPrice(args: {
  basePrice: string;
  size?: string;
  color?: ProductColorOption | null;
  sizePriceTiers?: SizePriceTier[];
  colorPriceTiers?: ColorPriceTier[];
}): string {
  const base = (args.basePrice || '').trim();
  const size = (args.size || '').trim().toLowerCase();
  if (size && args.sizePriceTiers?.length) {
    const tier = args.sizePriceTiers.find((t) =>
      t.sizes.some((s) => s.trim().toLowerCase() === size),
    );
    if (tier?.price && parseCommercePrice(tier.price) > 0) return tier.price.trim();
  }
  const colorLabel = (args.color?.label || '').trim().toLowerCase();
  if (colorLabel && args.colorPriceTiers?.length) {
    const tier = args.colorPriceTiers.find((t) =>
      t.colors.some((c) => c.trim().toLowerCase() === colorLabel),
    );
    if (tier?.price && parseCommercePrice(tier.price) > 0) return tier.price.trim();
  }
  const colorPrice = (args.color?.price || '').trim();
  if (colorPrice && parseCommercePrice(colorPrice) > 0) return colorPrice;
  return base;
}

/** Persist-ready colors: replace draft markers with stable display names. */
export function finalizeColorOptionsForPersist(raw: string): string {
  const options = parseColorOptions(raw);
  if (!options.length) return '';
  return serializeColorOptions(
    options.map((opt, index) => ({
      ...opt,
      label: (opt.label || '').trim() || `Color ${index + 1}`,
    })),
  );
}

export function colorOptionsHaveSwatches(options: ProductColorOption[]): boolean {
  return options.some((o) => Boolean(o.hex));
}

export function parseGalleryImageUrls(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw
    .split(/\n---\n/)
    .flatMap((block) => block.split(/\r?\n/))
    .map((s) => sanitizeProductImageUrl(s.trim()))
    .filter(Boolean);
}

export function serializeGalleryImages(urls: string[]): string {
  return urls.map((u) => u.trim()).filter(Boolean).join('\n---\n');
}

export function parseProductGallery(imageUrl: string, galleryRaw?: unknown): string[] {
  const all = [sanitizeProductImageUrl(imageUrl), ...parseGalleryImageUrls(galleryRaw)].filter(Boolean);
  return [...new Set(all)];
}

function normalizeMediaKey(url: string): string {
  return url
    .trim()
    .replace(/\/+$/, '')
    .replace(/\?.*$/, '')
    .toLowerCase();
}

/**
 * Gallery for PDP: primary + extras, then any color-linked images not already listed
 * so explicit `label|#hex|/media/...` mappings always have a selectable slide.
 */
export function buildProductGallery(
  imageUrl: string,
  galleryRaw: unknown,
  colorOptions: ProductColorOption[] = [],
): string[] {
  const base = parseProductGallery(imageUrl, galleryRaw);
  const seen = new Set(base.map(normalizeMediaKey));
  const out = [...base];
  for (const opt of colorOptions) {
    const url = sanitizeProductImageUrl(opt.imageUrl || '');
    if (!url) continue;
    const key = normalizeMediaKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}

/** Map selected color to a gallery index (explicit image URL, then positional fallback). */
export function resolveColorGalleryIndex(
  colorLabel: string,
  colorOptions: ProductColorOption[],
  gallery: string[],
  colorIndex?: number,
): number | null {
  if (gallery.length === 0) return null;
  const opt =
    typeof colorIndex === 'number' && colorIndex >= 0 && colorIndex < colorOptions.length
      ? colorOptions[colorIndex]
      : colorOptions.find((c) => c.label === colorLabel && Boolean(colorLabel));
  if (opt?.imageUrl) {
    const target = normalizeMediaKey(opt.imageUrl);
    const idx = gallery.findIndex((url) => normalizeMediaKey(url) === target);
    if (idx >= 0) return idx;
  }
  const resolvedIdx =
    typeof colorIndex === 'number' && colorIndex >= 0
      ? colorIndex
      : colorOptions.findIndex((c) => c.label === colorLabel);
  if (resolvedIdx >= 0 && resolvedIdx < gallery.length) return resolvedIdx;
  return null;
}

/** Inverse: gallery slide → color label when mapped by URL or unique position. */
export function resolveColorForGalleryIndex(
  galleryIndex: number,
  colorOptions: ProductColorOption[],
  gallery: string[],
): string | null {
  if (galleryIndex < 0 || galleryIndex >= gallery.length || colorOptions.length === 0) return null;
  const url = gallery[galleryIndex];
  const byUrl = colorOptions.find(
    (c) => c.imageUrl && normalizeMediaKey(c.imageUrl) === normalizeMediaKey(url),
  );
  if (byUrl) return byUrl.label;

  if (galleryIndex < colorOptions.length) {
    const opt = colorOptions[galleryIndex];
    const mapped = resolveColorGalleryIndex(opt.label, colorOptions, gallery);
    if (mapped === galleryIndex) return opt.label;
  }
  return null;
}

/**
 * When colors lack image URLs, bind each color to gallery[i] (stable encoding for CMS).
 * Colors beyond gallery length stay unbound (positional resolve still applies in-range).
 */
export function bindColorOptionsToGallery(
  colorOptions: ProductColorOption[],
  gallery: string[],
): ProductColorOption[] {
  if (!colorOptions.length || !gallery.length) return colorOptions;
  return colorOptions.map((opt, i) => {
    if (opt.imageUrl?.trim()) return opt;
    if (i >= gallery.length) return opt;
    return { ...opt, imageUrl: gallery[i] };
  });
}

export type SizeChartTable = { headers: string[]; rows: string[][] };

/** Parse pipe-separated size chart text into a table (returns null when not tabular). */
export function parseSizeChartTable(
  raw: string,
  opts?: { keepEmptyRows?: boolean },
): SizeChartTable | null {
  const lines = raw
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;
  const rows = lines.map((line) => line.split('|').map((cell) => cell.trim()).filter((cell, i, arr) => cell.length > 0 || i < arr.length));
  const normalized = opts?.keepEmptyRows ? rows : rows.filter((row) => row.some(Boolean));
  if (normalized.length < 2) return null;
  const colCount = Math.max(...normalized.map((row) => row.length));
  if (colCount < 2) return null;
  const padded = normalized.map((row) => {
    const next = [...row];
    while (next.length < colCount) next.push('');
    return next;
  });
  return { headers: padded[0], rows: padded.slice(1) };
}

export function serializeSizeChartTable(table: SizeChartTable): string {
  const lines = [table.headers, ...table.rows].map((row) => row.join(' | '));
  return lines.join('\n');
}

const DEFAULT_SIZE_CHART_HEADERS = ['Größe', 'Brust', 'Länge'];

/** Starter table for the product editor when sizeChart is empty or an image URL. */
export function emptySizeChartTable(headers: string[] = DEFAULT_SIZE_CHART_HEADERS): SizeChartTable {
  const cols = headers.length >= 2 ? [...headers] : [...DEFAULT_SIZE_CHART_HEADERS];
  return {
    headers: cols,
    rows: [cols.map(() => '')],
  };
}

/**
 * Editor-friendly size-chart parse: never null.
 * Image URLs and empty/invalid text fall back to a starter table (not written until the user edits).
 */
export function ensureSizeChartTable(raw: string, fallbackHeaders?: string[]): SizeChartTable {
  const fallback = emptySizeChartTable(fallbackHeaders);
  const s = raw.trim();
  if (!s || isSizeChartImageUrl(s)) return fallback;
  const parsed = parseSizeChartTable(s, { keepEmptyRows: true });
  if (parsed) return parsed;
  const lines = s.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 1 && lines[0].includes('|')) {
    const headers = lines[0].split('|').map((cell) => cell.trim());
    while (headers.length < 2) headers.push('');
    return { headers, rows: [headers.map(() => '')] };
  }
  return fallback;
}

const TAB_DRAFT_RE = /^__draft_(\d+)__$/i;

export function parseProductTabs(tabContent: string, description: string, descriptionLabel: string): TabItem[] {
  const trimmed = tabContent.trim();
  if (trimmed) {
    return parseTabItems(trimmed).tabs
      .map((tab) => ({
        ...tab,
        label: TAB_DRAFT_RE.test(tab.label) ? '' : tab.label,
      }))
      .filter((t) => t.label || t.content);
  }
  if (description.trim()) {
    return [{ label: descriptionLabel, content: description.trim() }];
  }
  return [];
}

export function serializeProductTabs(tabs: Array<{ label: string; content: string }>): string {
  const cleaned = tabs
    .map((tab, index) => {
      const content = tab.content.trim();
      const label = tab.label.trim() || (content ? `__draft_${index + 1}__` : '');
      return { label, content };
    })
    .filter((tab) => tab.label || tab.content);
  if (!cleaned.length) return '';
  return serializeTabItems(cleaned).content;
}

export function finalizeProductTabsForPersist(tabContent: string): string {
  const tabs = parseProductTabs(tabContent, '', 'Description');
  if (!tabs.length) return '';
  return serializeProductTabs(
    tabs.map((tab, index) => ({
      ...tab,
      label: tab.label.trim() || `Tab ${index + 1}`,
    })),
  );
}

/** Case-insensitive key for matching product tab labels across global / per-product scopes. */
export function productTabLabelKey(label: string): string {
  return label.trim().toLowerCase();
}

export type ProductTabOrderEntry = {
  scope: 'global' | 'product';
  key: string;
};

/** One line per slot: `g:versand` or `p:beschreibung` (key is case-insensitive label). */
export function parseProductTabOrder(raw: string): ProductTabOrderEntry[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const out: ProductTabOrderEntry[] = [];
  for (const line of trimmed.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    const m = /^(g|global|p|product):(.+)$/i.exec(t);
    if (!m) continue;
    const scope: ProductTabOrderEntry['scope'] = m[1].toLowerCase().startsWith('g') ? 'global' : 'product';
    const key = productTabLabelKey(m[2]);
    if (key) out.push({ scope, key });
  }
  return out;
}

export function serializeProductTabOrder(entries: ProductTabOrderEntry[]): string {
  return entries
    .filter((e) => e.key)
    .map((e) => `${e.scope === 'global' ? 'g' : 'p'}:${e.key}`)
    .join('\n');
}

export function buildDefaultProductTabOrder(
  globalTabContent: string,
  productTabContent: string,
  description = '',
  descriptionLabel = 'Description',
): ProductTabOrderEntry[] {
  const globalMap = buildProductTabKeyMap(globalTabContent, '', descriptionLabel);
  const productMap = buildProductTabKeyMap(productTabContent, description, descriptionLabel);
  const entries: ProductTabOrderEntry[] = [];
  for (const key of globalMap.keys()) {
    entries.push({ scope: 'global', key });
  }
  for (const key of productMap.keys()) {
    if (!globalMap.has(key)) entries.push({ scope: 'product', key });
  }
  return entries;
}

function productTabOrderSlotKey(entry: ProductTabOrderEntry): string {
  return `${entry.scope}:${entry.key}`;
}

/** Map storage keys (incl. `__draft_N__`) to display tabs. */
function buildProductTabKeyMap(
  tabContent: string,
  description: string,
  descriptionLabel: string,
): Map<string, TabItem> {
  const trimmed = tabContent.trim();
  const rawTabs = trimmed
    ? parseTabItems(trimmed).tabs
    : description.trim()
      ? [{ label: descriptionLabel, content: description.trim() }]
      : [];
  const map = new Map<string, TabItem>();
  for (const raw of rawTabs) {
    const key = productTabLabelKey(raw.label);
    if (!key) continue;
    map.set(key, {
      label: TAB_DRAFT_RE.test(raw.label) ? '' : raw.label.trim(),
      content: raw.content,
    });
  }
  return map;
}

export function productTabStorageKeyAt(serialized: string, index: number): string {
  const raw = parseTabItems(serialized.trim()).tabs[index];
  return raw ? productTabLabelKey(raw.label) : '';
}

/** Swap a tab slot key in order (keys are already normalized). */
export function renameProductTabOrderKeyByKey(
  tabOrder: string,
  scope: 'global' | 'product',
  oldKey: string,
  newKey: string,
): string {
  if (!oldKey || !newKey || oldKey === newKey) return tabOrder;
  return serializeProductTabOrder(
    parseProductTabOrder(tabOrder).map((entry) => (
      entry.scope === scope && entry.key === oldKey
        ? { ...entry, key: newKey }
        : entry
    )),
  );
}

export type ResolvedProductPageTab = TabItem & {
  scope: 'global' | 'product';
  key: string;
};

export function resolveProductPageTabs(params: {
  globalTabContent: string;
  productTabContent: string;
  tabOrder?: string;
  defaultTabOrder?: string;
  description?: string;
  descriptionLabel?: string;
}): ResolvedProductPageTab[] {
  const {
    globalTabContent,
    productTabContent,
    tabOrder = '',
    defaultTabOrder = '',
    description = '',
    descriptionLabel = 'Description',
  } = params;

  const globalByKey = buildProductTabKeyMap(globalTabContent, '', descriptionLabel);
  const globalKeys = new Set(globalByKey.keys());
  const productByKey = buildProductTabKeyMap(productTabContent, description, descriptionLabel);
  for (const key of globalKeys) {
    productByKey.delete(key);
  }

  const explicitOrder = parseProductTabOrder(tabOrder);
  const order = explicitOrder.length
    ? explicitOrder
    : parseProductTabOrder(defaultTabOrder).length
      ? parseProductTabOrder(defaultTabOrder)
      : buildDefaultProductTabOrder(globalTabContent, productTabContent, description, descriptionLabel);

  const used = new Set<string>();
  const result: ResolvedProductPageTab[] = [];

  for (const entry of order) {
    const slot = productTabOrderSlotKey(entry);
    if (used.has(slot)) continue;
    const source = entry.scope === 'global' ? globalByKey.get(entry.key) : productByKey.get(entry.key);
    if (!source) continue;
    result.push({ ...source, scope: entry.scope, key: entry.key });
    used.add(slot);
  }

  for (const [key, tab] of globalByKey) {
    const slot = `global:${key}`;
    if (!used.has(slot)) {
      result.push({ ...tab, scope: 'global', key });
      used.add(slot);
    }
  }
  for (const [key, tab] of productByKey) {
    const slot = `product:${key}`;
    if (!used.has(slot)) {
      result.push({ ...tab, scope: 'product', key });
      used.add(slot);
    }
  }

  return result;
}

/**
 * Legacy name — resolves tabs using optional per-product / shop-default order.
 * Without order, globals and products are interleaved only via explicit tabOrder.
 */
export function mergeGlobalAndProductTabs(
  globalTabContent: string,
  productTabContent: string,
  description = '',
  descriptionLabel = 'Description',
  tabOrder = '',
  defaultTabOrder = '',
): TabItem[] {
  return resolveProductPageTabs({
    globalTabContent,
    productTabContent,
    tabOrder,
    defaultTabOrder,
    description,
    descriptionLabel,
  });
}

/** Serialized form of {@link resolveProductPageTabs} for PDP / preview `tabContent` props. */
export function mergeGlobalAndProductTabContent(
  globalTabContent: string,
  productTabContent: string,
  description = '',
  descriptionLabel = 'Description',
  tabOrder = '',
  defaultTabOrder = '',
): string {
  return serializeProductTabs(
    mergeGlobalAndProductTabs(
      globalTabContent,
      productTabContent,
      description,
      descriptionLabel,
      tabOrder,
      defaultTabOrder,
    ),
  );
}

/** Rebuild order after drag-and-drop on the unified tab list. */
export function productTabOrderFromResolvedTabs(
  tabs: Array<{ scope: 'global' | 'product'; key: string }>,
): string {
  return serializeProductTabOrder(
    tabs.map((tab) => ({ scope: tab.scope, key: tab.key })),
  );
}

/** Swap a product tab key in order when its label changes. */
export function renameProductTabOrderKey(
  tabOrder: string,
  scope: 'global' | 'product',
  oldLabel: string,
  newLabel: string,
): string {
  const oldKey = productTabLabelKey(oldLabel);
  const newKey = productTabLabelKey(newLabel);
  if (!oldKey || !newKey || oldKey === newKey) return tabOrder;
  return serializeProductTabOrder(
    parseProductTabOrder(tabOrder).map((entry) => (
      entry.scope === scope && entry.key === oldKey
        ? { ...entry, key: newKey }
        : entry
    )),
  );
}

/** Drop product slots from order when a product-only tab is deleted. */
export function removeProductTabOrderKey(tabOrder: string, label: string): string {
  const key = productTabLabelKey(label);
  if (!key) return tabOrder;
  return serializeProductTabOrder(
    parseProductTabOrder(tabOrder).filter(
      (entry) => !(entry.scope === 'product' && entry.key === key),
    ),
  );
}

/** Append a product tab slot to order (e.g. after add). */
export function appendProductTabOrderKey(tabOrder: string, label: string): string {
  const key = productTabLabelKey(label);
  if (!key) return tabOrder;
  const entries = parseProductTabOrder(tabOrder);
  if (entries.some((e) => e.scope === 'product' && e.key === key)) return tabOrder;
  return serializeProductTabOrder([...entries, { scope: 'product', key }]);
}

/** Append a global tab slot to shop default order (e.g. after add). */
export function appendGlobalTabOrderKey(defaultTabOrder: string, label: string): string {
  const key = productTabLabelKey(label);
  if (!key) return defaultTabOrder;
  const entries = parseProductTabOrder(defaultTabOrder);
  if (entries.some((e) => e.scope === 'global' && e.key === key)) return defaultTabOrder;
  return serializeProductTabOrder([...entries, { scope: 'global', key }]);
}

/** Drop a global slot from shop default order when a global tab is deleted. */
export function removeGlobalTabOrderKey(defaultTabOrder: string, label: string): string {
  const key = productTabLabelKey(label);
  if (!key) return defaultTabOrder;
  return serializeProductTabOrder(
    parseProductTabOrder(defaultTabOrder).filter(
      (entry) => !(entry.scope === 'global' && entry.key === key),
    ),
  );
}

/** Reorder global keys inside shop default order (product slots keep relative positions). */
export function reorderDefaultProductTabOrderGlobals(
  defaultTabOrder: string,
  globalLabelKeysInOrder: string[],
): string {
  const prev = parseProductTabOrder(defaultTabOrder);
  const productEntries = prev.filter((e) => e.scope === 'product');
  const globalEntries = globalLabelKeysInOrder
    .map((key) => productTabLabelKey(key))
    .filter(Boolean)
    .map((key) => ({ scope: 'global' as const, key }));
  const seen = new Set(globalEntries.map((e) => e.key));
  for (const entry of prev) {
    if (entry.scope === 'global' && !seen.has(entry.key)) {
      globalEntries.push({ scope: 'global', key: entry.key });
      seen.add(entry.key);
    }
  }
  return serializeProductTabOrder([...globalEntries, ...productEntries]);
}

/** Drop tabs whose label matches (case-insensitive). Empty label never matches. */
export function removeProductTabsByLabel(tabContent: string, label: string): string {
  const key = productTabLabelKey(label);
  if (!key) return tabContent;
  const tabs = parseProductTabs(tabContent, '', 'Description');
  return serializeProductTabs(tabs.filter((tab) => productTabLabelKey(tab.label) !== key));
}

export function isSizeChartImageUrl(raw: string): boolean {
  const s = raw.trim();
  if (!s) return false;
  if (IMAGE_URL_RE.test(s)) return true;
  return /\.(png|jpe?g|webp|gif|svg)(?:\?|#|$)/i.test(s);
}

/** Compact pill styling for unusually long color names (after care stripping). */
export function isLongProductColorLabel(label: string, maxChars = 28): boolean {
  return splitProductColorLabel(label).label.trim().length > maxChars;
}

/** JustBrand-style gallery view slots mistaken for colors (Hauptansicht / Ansicht N). */
export function isGalleryViewColorLabel(label: string): boolean {
  const s = label.trim();
  if (!s) return false;
  return /^hauptansicht$/i.test(s) || /^ansicht\s*\d+$/i.test(s);
}

export function colorOptionsAreGalleryViews(options: ProductColorOption[]): boolean {
  return options.length > 0 && options.every((o) => isGalleryViewColorLabel(o.label || ''));
}

/** Normalize pipe/table size-chart text for legacy `<pre>` display. */
export function formatSizeChartText(raw: string): string {
  return raw
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}

export function productHasVariants(data: Record<string, unknown> | undefined): boolean {
  if (!data) return false;
  const sizes = typeof data.sizes === 'string' ? data.sizes.trim() : '';
  const colors = typeof data.colors === 'string' ? data.colors.trim() : '';
  return Boolean(sizes || colors);
}

/** Stable product slug from a display name (German umlauts transliterated). */
export function slugifyProductName(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export type ProductFormState = {
  name: string;
  slug: string;
  price: string;
  compareAtPrice: string;
  /**
   * Tax hint beside price on storefront. `inherit` uses shop `commerce.priceTaxDisplay`.
   */
  priceTaxDisplay: 'inherit' | 'none' | 'incl' | 'excl';
  /** Optional 30-day lowest price (shown in tax modal when set). */
  priceLow30d: string;
  /** Inherit shop tax modal copy, or custom for this product. */
  taxModalMode: 'inherit' | 'custom';
  taxModalTitle: string;
  taxModalBody: string;
  taxModalMoreInfoUrl: string;
  taxModalMoreInfoLabel: string;
  shortDescription: string;
  description: string;
  link: string;
  imageUrl: string;
  category: string;
  /** Category-tree node id (see Category model). Empty = unassigned. Independent of legacy `category` text. */
  categoryId: string;
  sizes: string;
  /** Multiline: `S, M, L | 20.95` per line — see parseSizePriceTiers. */
  sizePriceTiers: string;
  colors: string;
  /** Multiline: `Pink, Schwarz | 24.99` per line — see parseColorPriceTiers. */
  colorPriceTiers: string;
  galleryImages: string;
  galleryAnimation: string;
  /** Touch swipe on the PDP main gallery (mobile). Default true when unset. */
  gallerySwipeable: boolean;
  /**
   * Gallery slide → color/options sync. `inherit` uses shop `commerce.galleryOptionsSync`
   * (shop default false). Explicit true/false overrides per product.
   */
  galleryOptionsSync: GalleryOptionsSyncMode;
  tabContent: string;
  /** Per-product PDP tab order (`g:label` / `p:label` lines). Empty = shop default or auto. */
  tabOrder: string;
  sizeChart: string;
  /** Editor-only cache so image↔table mode switches do not lose the other representation. */
  sizeChartImage: string;
  /** Editor-only cache for pipe-table text while sizeChart holds an image URL (or vice versa). */
  sizeChartTable: string;
  sku: string;
  stock: string;
  seoTitle: string;
  seoDescription: string;
  featured: boolean;
  /** When true, product stays in admin but is omitted from frontpage/catalog/PDP. */
  hidden: boolean;
  status: 'draft' | 'published';
};

export function emptyProductForm(): ProductFormState {
  return {
    name: '',
    slug: '',
    price: '',
    compareAtPrice: '',
    priceTaxDisplay: 'inherit',
    priceLow30d: '',
    taxModalMode: 'inherit',
    taxModalTitle: '',
    taxModalBody: '',
    taxModalMoreInfoUrl: '',
    taxModalMoreInfoLabel: '',
    shortDescription: '',
    description: '',
    link: '',
    imageUrl: '',
    category: '',
    categoryId: '',
    sizes: '',
    sizePriceTiers: '',
    colors: '',
    colorPriceTiers: '',
    galleryImages: '',
    galleryAnimation: '',
    gallerySwipeable: true,
    galleryOptionsSync: 'inherit',
    tabContent: '',
    tabOrder: '',
    sizeChart: '',
    sizeChartImage: '',
    sizeChartTable: '',
    sku: '',
    stock: '',
    seoTitle: '',
    seoDescription: '',
    featured: false,
    hidden: false,
    status: 'draft',
  };
}

export function productFormFromItem(item: {
  slug: string;
  status: string;
  data?: Record<string, unknown>;
}): ProductFormState {
  const d = item.data || {};
  const sizeChart = String(d.sizeChart || '');
  const galleryAnimation =
    typeof d.galleryAnimation === 'string'
      ? d.galleryAnimation
      : (typeof d.gallery_animation === 'string'
        ? d.gallery_animation
        : (typeof d.slideAnimation === 'string' ? d.slideAnimation : ''));
  const gallerySwipeable = parseGallerySwipeable(
    d.gallerySwipeable ?? d.gallery_swipeable ?? d.swipeableGallery,
  );
  const galleryOptionsSync = parseGalleryOptionsSyncMode(
    d.galleryOptionsSync ?? d.gallery_options_sync ?? d.syncGalleryWithVariants,
  );
  const priceTaxRaw = String(d.priceTaxDisplay || '').trim().toLowerCase();
  const priceTaxDisplay =
    priceTaxRaw === 'none' || priceTaxRaw === 'incl' || priceTaxRaw === 'excl' || priceTaxRaw === 'inherit'
      ? priceTaxRaw
      : 'inherit';
  const taxModalModeRaw = String(d.taxModalMode || '').trim().toLowerCase();
  const taxModalMode = taxModalModeRaw === 'custom' ? 'custom' : 'inherit';
  return {
    name: String(d.name || ''),
    slug: item.slug,
    price: String(d.price || ''),
    compareAtPrice: String(d.compareAtPrice || ''),
    priceTaxDisplay,
    priceLow30d: String(d.priceLow30d || ''),
    taxModalMode,
    taxModalTitle: String(d.taxModalTitle || ''),
    taxModalBody: String(d.taxModalBody || ''),
    taxModalMoreInfoUrl: String(d.taxModalMoreInfoUrl || ''),
    taxModalMoreInfoLabel: String(d.taxModalMoreInfoLabel || ''),
    shortDescription: String(d.shortDescription || d.short_description || ''),
    description: String(d.description || ''),
    link: String(d.link || ''),
    imageUrl: String(d.imageUrl || ''),
    category: String(d.category || ''),
    categoryId: String(d.categoryId || ''),
    sizes: String(d.sizes || ''),
    sizePriceTiers: String(d.sizePriceTiers || ''),
    colors: String(d.colors || ''),
    colorPriceTiers: String(d.colorPriceTiers || ''),
    galleryImages: String(d.galleryImages || ''),
    galleryAnimation,
    gallerySwipeable,
    galleryOptionsSync,
    tabContent: String(d.tabContent || ''),
    tabOrder: String(d.tabOrder || d.tab_order || ''),
    sizeChart,
    sizeChartImage: isSizeChartImageUrl(sizeChart) ? sizeChart : '',
    sizeChartTable: sizeChart && !isSizeChartImageUrl(sizeChart) ? sizeChart : '',
    sku: String(d.sku || ''),
    stock: d.stock === undefined || d.stock === null ? '' : String(d.stock),
    seoTitle: String(d.seoTitle || ''),
    seoDescription: String(d.seoDescription || ''),
    featured: d.featured === true || d.featured === 'true',
    hidden: d.hidden === true || d.hidden === 'true',
    status: item.status === 'published' ? 'published' : 'draft',
  };
}

export function productFormToPayload(form: ProductFormState): {
  slug: string;
  status: 'draft' | 'published';
  data: Record<string, unknown>;
} {
  // Capture care glued onto color names before finalize strips it from labels.
  const careFromColors = extractCareNotesFromColorField(form.colors);
  const data: Record<string, unknown> = {
    name: form.name.trim(),
    price: form.price.trim(),
    description: form.description.trim(),
    link: form.link.trim(),
    imageUrl: form.imageUrl.trim(),
    category: form.category.trim(),
    // Always send (even empty) so clearing the tree assignment persists on replace-style update.
    categoryId: form.categoryId.trim(),
    sizes: form.sizes.trim(),
    sizePriceTiers: serializeSizePriceTiers(parseSizePriceTiers(form.sizePriceTiers)),
    colors: finalizeColorOptionsForPersist(form.colors),
    colorPriceTiers: serializeColorPriceTiers(parseColorPriceTiers(form.colorPriceTiers)),
    galleryImages: form.galleryImages.trim(),
    // Always persist so public PDP can distinguish Standard vs Würfel/etc. after save
    // (omit-on-empty left older publishes without the field and fell back to plain <img>).
    galleryAnimation: (form.galleryAnimation.trim() || 'default'),
    // Always persist so owners can turn swipe off (default remains true when unset on load).
    gallerySwipeable: form.gallerySwipeable !== false,
    tabContent: finalizeProductTabsForPersist(
      enrichTabContentWithCareNotes(form.tabContent, careFromColors),
    ),
    tabOrder: form.tabOrder.trim(),
    sizeChart: form.sizeChart.trim(),
  };
  // Persist override when not inherit so shop default applies when unset/inherit.
  if (form.galleryOptionsSync === true || form.galleryOptionsSync === false) {
    data.galleryOptionsSync = form.galleryOptionsSync;
  }
  if (form.compareAtPrice.trim()) data.compareAtPrice = form.compareAtPrice.trim();
  // Persist override when not inherit so shop default applies when unset/inherit.
  if (form.priceTaxDisplay && form.priceTaxDisplay !== 'inherit') {
    data.priceTaxDisplay = form.priceTaxDisplay;
  }
  if (form.priceLow30d.trim()) data.priceLow30d = form.priceLow30d.trim();
  if (form.taxModalMode === 'custom') {
    data.taxModalMode = 'custom';
    if (form.taxModalTitle.trim()) data.taxModalTitle = form.taxModalTitle.trim();
    if (form.taxModalBody.trim()) data.taxModalBody = form.taxModalBody.trim();
    if (form.taxModalMoreInfoUrl.trim()) data.taxModalMoreInfoUrl = form.taxModalMoreInfoUrl.trim();
    if (form.taxModalMoreInfoLabel.trim()) data.taxModalMoreInfoLabel = form.taxModalMoreInfoLabel.trim();
  }
  if (form.shortDescription.trim()) data.shortDescription = form.shortDescription.trim();
  if (form.sku.trim()) data.sku = form.sku.trim();
  if (form.stock.trim()) data.stock = form.stock.trim();
  if (form.seoTitle.trim()) data.seoTitle = form.seoTitle.trim();
  if (form.seoDescription.trim()) data.seoDescription = form.seoDescription.trim();
  // Always include so update replace can clear a previously featured/hidden product.
  data.featured = form.featured === true;
  data.hidden = form.hidden === true;
  return {
    slug: form.slug.trim() || slugifyProductName(form.name),
    status: form.status,
    data,
  };
}

const PRODUCT_CARD_DESC_MAX = 160;

/** Card teaser: prefer shortDescription, else truncate long description. */
export function productCardDescription(
  data: Record<string, unknown> | null | undefined,
  maxLen = PRODUCT_CARD_DESC_MAX,
): string {
  const short = String(data?.shortDescription ?? data?.short_description ?? '').trim();
  if (short) return short;
  const long = String(data?.description ?? '').trim();
  if (!long) return '';
  if (long.length <= maxLen) return long;
  const cut = long.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${base.trimEnd()}…`;
}

/** Positive price amounts from base, size/color tiers, and color prices (for listing cards). */
export function collectProductPriceAmounts(
  data: Record<string, unknown> | null | undefined,
): number[] {
  if (!data) return [];
  const amounts: number[] = [];
  const push = (raw: unknown) => {
    const n = parseCommercePrice(String(raw ?? ''));
    if (n > 0) amounts.push(n);
  };
  push(data.price);
  for (const tier of parseSizePriceTiers(data.sizePriceTiers)) push(tier.price);
  for (const tier of parseColorPriceTiers(data.colorPriceTiers)) push(tier.price);
  for (const color of parseColorOptions(data.colors)) push(color.price);
  return amounts;
}

export function productCardMinPrice(
  data: Record<string, unknown> | null | undefined,
): number {
  const amounts = collectProductPriceAmounts(data);
  return amounts.length ? Math.min(...amounts) : 0;
}

/**
 * Listing/card price: single amount, or German-friendly “ab …” from the lowest
 * variant/base amount when size tiers / color prices differ.
 * Money formatting matches formatCommerceMoney (locale + currency position when provided).
 * Falls back to displayCommercePrice for non-numeric base strings.
 */
export function formatProductCardPrice(
  data: Record<string, unknown> | null | undefined,
  currency = '€',
  options?: {
    fromPrefix?: string;
    locale?: string | null;
    currencyPosition?: 'auto' | 'left' | 'right' | null;
  },
): string {
  const moneyOpts =
    options?.locale != null || options?.currencyPosition != null
      ? { locale: options.locale, currencyPosition: options.currencyPosition }
      : undefined;
  const amounts = collectProductPriceAmounts(data);
  if (!amounts.length) {
    return displayCommercePrice(String(data?.price ?? ''), currency, moneyOpts);
  }
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  if (min === max) return formatCommerceMoney(min, currency, moneyOpts);
  const fromPrefix = (options?.fromPrefix ?? 'ab').trim() || 'ab';
  return `${fromPrefix} ${formatCommerceMoney(min, currency, moneyOpts)}`;
}

/** Pick a unique slug for a product duplicate (-copy, -copy-2, …). */
export function uniqueProductCopySlug(baseSlug: string, takenSlugs: Iterable<string>): string {
  const taken = new Set(takenSlugs);
  const root = (baseSlug || 'product').replace(/(-copy(-\d+)?)+$/i, '') || baseSlug || 'product';
  let candidate = `${root}-copy`;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${root}-copy-${n}`;
    n += 1;
  }
  return candidate;
}

/** Parse stock field — null means unlimited/unknown; 0 means out of stock. */
export function parseProductStock(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  if (s === 'out' || s === 'oos' || s === 'unavailable') return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}
